import {
  Body,
  Button,
  CustomLink,
  Flex,
  LazyImage,
  Modal,
  Shape,
  styled,
} from '@teko/ds-desktop';
import { ChevronForwardFilled } from '@teko/ds-icon';
import { RelatedPosts, RelatedVideos } from '@teko/types/ecommerce';
import { t } from 'i18n';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useState } from 'react';

const YoutubeFrame = dynamic(() =>
  import('./YoutubeFrame').then((mod) => mod.YoutubeFrame),
);

const MAX_ITEMS_WITH_TWO_BLOCKS = 3;

interface RelatedVideosAndPostsProps {
  relatedVideos?: RelatedVideos;
  relatedPosts?: RelatedPosts;
  isMobile?: boolean;
}

const StyledH3 = styled.h3`
  font-size: 24px;
  line-height: 32px;
`;

const IFramePopupWrapper = styled.div`
  width: 100%;
  height: 540px;
  background-color: black;
`;

const StyledCustomLink = styled(CustomLink)`
  width: 100%;
`;

const RelatedBlock: React.FC<{
  type: string;
  title: string;
  seeMoreUrl?: string;
  items: Array<{ url: string; thumbnailUrl: string; description: string }>;
}> = (props) => {
  const [youtubeLink, setYoutubeLink] = useState<string>();
  const { title, type, seeMoreUrl, items } = props;
  const renderItem = (url: string, description: string) => {
    return (
      <Flex gap="s8" align="center">
        <LazyImage
          height="56px"
          width="100px"
          borderRadius={6}
          contain
          src={url}
          imageWidth={200}
        />
        <Shape width="calc(100% - 100px - 16px)">
          <Body line={3} level={1}>
            {description}
          </Body>
        </Shape>
      </Flex>
    );
  };
  return (
    <>
      <Shape padding="s16">
        <StyledH3>{title}</StyledH3>
        <Flex gap="s12" direction="column" marginTop="s12">
          {items.map((item, index) => {
            const { url, thumbnailUrl, description } = item;
            return type === 'post' ? (
              <StyledCustomLink
                href={url}
                target="_blank"
                key={`${index}${url}`}
              >
                {renderItem(thumbnailUrl, description)}
              </StyledCustomLink>
            ) : (
              <Shape
                onClick={() => setYoutubeLink(url)}
                style={{ cursor: 'pointer', width: '100%' }}
                key={`${index}${url}`}
              >
                {renderItem(thumbnailUrl, description)}
              </Shape>
            );
          })}
        </Flex>
        {!!seeMoreUrl && (
          <CustomLink href={seeMoreUrl} target="_blank">
            <Flex paddingTop="s16" align="center" justify="center">
              <Button color="link" style={{ border: 'none' }}>
                {t('Xem thêm nội dung')}
                <ChevronForwardFilled />
              </Button>
            </Flex>
          </CustomLink>
        )}
      </Shape>

      <Modal
        visible={!!youtubeLink}
        width="960px"
        onClose={() => setYoutubeLink('')}
        showCloseIcon={false}
        footer={false}
        contentStyle={{ padding: '0px' }}
      >
        <IFramePopupWrapper>
          <YoutubeFrame youtubeLink={youtubeLink} />
        </IFramePopupWrapper>
      </Modal>
    </>
  );
};

export const RelateVideosAndPosts: React.FC<RelatedVideosAndPostsProps> = (
  props,
) => {
  const { relatedPosts, relatedVideos } = props;
  return (
    <>
      {!!relatedVideos?.urls?.length && (
        <Head>
          {relatedVideos.urls.map((video, index) => {
            return (
              <script
                key={`${index}${video.url}`}
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'VideoObject',
                    name: video.name,
                    description: video.description,
                    thumbnailUrl: video.thumbnailUrl,
                    uploadDate:
                      typeof video.uploadDate === 'number'
                        ? new Date(video.uploadDate * 1000).toISOString()
                        : '',
                    embedUrl: video.url,
                  }),
                }}
              />
            );
          })}
        </Head>
      )}
      {!!relatedPosts?.urls?.length && (
        <Shape
          marginTop="s12"
          marginBottom={!!relatedVideos?.urls?.length ? 's0' : 's12'}
          backgroundColor="white"
          borderRadius="m"
        >
          <RelatedBlock
            title={relatedPosts.title || ''}
            type="post"
            seeMoreUrl={relatedPosts.seeMoreUrl}
            items={relatedPosts.urls
              .slice(0, MAX_ITEMS_WITH_TWO_BLOCKS)
              .map(({ url, thumbnailUrl, description }) => {
                return {
                  url: url || '',
                  thumbnailUrl: thumbnailUrl || '',
                  description: description || '',
                };
              })}
          />
        </Shape>
      )}
      {!!relatedVideos?.urls?.length && (
        <Shape
          margin={['s12', 's0']}
          backgroundColor="white"
          border="s8"
          borderRadius="m"
        >
          <RelatedBlock
            title={relatedVideos.title || ''}
            type="video"
            seeMoreUrl={relatedVideos.seeMoreUrl}
            items={relatedVideos.urls
              .slice(0, MAX_ITEMS_WITH_TWO_BLOCKS)
              .map(({ url, thumbnailUrl, name }) => {
                return {
                  url: url || '',
                  thumbnailUrl: thumbnailUrl || '',
                  description: name || '',
                };
              })}
          />
        </Shape>
      )}
    </>
  );
};
